import { Injectable } from '@angular/core';
import {
  ActiveCheckoutRecommendationRule,
  CheckoutRecommendationItem,
} from './customer-checkout-recommendations.helpers';

/**
 * Per-order cache of the recommended items resolved for each rule's page (#9303).
 *
 * <p>The recommendation page component is mounted via an `ngSwitch` on the storefront view-state
 * ({@code store-loading.component.html}), so navigating to the item-detail page to add an item and
 * returning **destroys and recreates** the component — its in-memory `rawItems` are lost and a
 * naïve re-activation would re-resolve against the now-updated basket and drop the very category
 * the customer just added to. Spec §"Add Item Flow": the list "is computed on activation and stays
 * stable for the duration of the visit, even as the basket changes underneath". This singleton
 * holds that list across the detour so the page reuses it instead of recomputing.
 *
 * <p>The cache is scoped to a single order: any change of order uuid clears it. This is essential
 * on kiosk, where one long-lived session creates thousands of orders with no page refresh — keying
 * by order uuid both implements the "clear on new order" rule and bounds the map to the current
 * order's handful of rules, so it can never grow without limit.
 */
@Injectable({ providedIn: 'root' })
export class CustomerCheckoutRecommendationsCacheService {
  private orderUuid: string | null = null;
  private readonly byRuleId = new Map<number, CheckoutRecommendationItem[]>();
  /** Rule metadata keyed by ruleId — persists across component destroy/recreate so the
   *  page can recover `rule.question` on browser back-navigation (#10374). */
  private readonly rulesById = new Map<
    number,
    ActiveCheckoutRecommendationRule
  >();

  /**
   * Cached resolved items for a rule within the given order, or `null` on a miss. A different
   * order uuid is treated as a brand-new order and clears the cache before answering.
   */
  get(
    orderUuid: string | null,
    ruleId: number,
  ): CheckoutRecommendationItem[] | null {
    this.ensureOrder(orderUuid);
    return this.byRuleId.get(ruleId) ?? null;
  }

  /** Store the stable list resolved for a rule within the given order. */
  set(
    orderUuid: string | null,
    ruleId: number,
    items: CheckoutRecommendationItem[],
  ): void {
    this.ensureOrder(orderUuid);
    this.byRuleId.set(ruleId, items);
  }

  /**
   * Rule metadata for a given ruleId, or `null` on a miss. Populated by
   * {@link setRules} when the coordinator loads the active-rules list at flow start.
   * Used as a last-resort fallback in the page component when Angular's router state
   * is absent (browser back-navigation destroys and recreates the component).
   */
  getRule(ruleId: number): ActiveCheckoutRecommendationRule | null {
    return this.rulesById.get(ruleId) ?? null;
  }

  /**
   * Cache the full active-rules list for the current flow. Called by the coordinator
   * immediately after the API response arrives so the data is available even if the
   * page component is later destroyed and recreated (e.g. on browser back-navigation).
   * Replaces any previously cached rules.
   */
  setRules(rules: ActiveCheckoutRecommendationRule[]): void {
    this.rulesById.clear();
    for (const rule of rules) {
      this.rulesById.set(rule.ruleId, rule);
    }
  }

  /**
   * Drop everything. Called when a new recommendation flow starts (the customer presses
   * "Proceed to checkout" from the catalog) so each flow recomputes from scratch.
   */
  reset(orderUuid: string | null = null): void {
    this.byRuleId.clear();
    this.rulesById.clear();
    this.orderUuid = orderUuid;
  }

  /**
   * A new order uuid invalidates the whole cache. This is the primary "clear on new order"
   * safeguard and what keeps a never-refreshed kiosk session from accumulating stale per-rule
   * entries across every order it processes.
   */
  private ensureOrder(orderUuid: string | null): void {
    if (orderUuid !== this.orderUuid) {
      this.byRuleId.clear();
      this.orderUuid = orderUuid;
    }
  }
}
